// widgetSettings — per-widget user settings layer.
//
// Each widget can declare an optional `settingsSchema` (list of fields) on
// its WidgetDefinition. The user edits those values via WidgetSettingsEditor
// inside the SidebarWidgetInspector (Tab 3). Values persist to localStorage
// under a single key keyed by widget id, with cross-component invalidation
// via a window event.
//
// Pattern mirrors src/lib/app-launcher/userAppGradients.ts — same store /
// event / hook shape. Stays per-browser; no Supabase sync (yet). When a
// widget wants to consume its settings at runtime, it calls
// useWidgetSettings(widgetId) and reads the value off the returned object.

import { useEffect, useState } from "react";

const STORAGE_KEY = "cc-widget-settings-v1";
const EVENT_NAME = "cc-widget-settings-change";
const BROADCAST_CHANNEL_NAME = "cc-widget-settings-sync";

// Lazy-init BroadcastChannel — only when needed in the browser, SSR-safe.
let broadcastChannel: BroadcastChannel | null = null;
function getBroadcastChannel(): BroadcastChannel | null {
  if (typeof window === "undefined" || typeof BroadcastChannel === "undefined") return null;
  if (!broadcastChannel) broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
  return broadcastChannel;
}

export type WidgetFieldType = "text" | "number" | "boolean" | "select" | "color" | "url" | "multiselect";

export interface WidgetSelectOption {
  value: string;
  label: { he: string; en: string; ru?: string };
}

export interface WidgetFieldSchema {
  key: string;
  label: { he: string; en: string; ru?: string };
  description?: { he: string; en: string; ru?: string };
  type: WidgetFieldType;
  /** Default applied when the user hasn't set a value yet. */
  default?: string | number | boolean | string[];
  /** Required for type="select" or type="multiselect". */
  options?: WidgetSelectOption[];
}

export type WidgetSettingsMap = Record<string, Record<string, unknown>>;
export type WidgetSettings = Record<string, unknown>;

/** Load the full settings map. Returns {} on SSR or parse error. */
export function loadAllWidgetSettings(): WidgetSettingsMap {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    const out: WidgetSettingsMap = {};
    for (const [widgetId, settings] of Object.entries(parsed as Record<string, unknown>)) {
      if (settings && typeof settings === "object" && !Array.isArray(settings)) {
        out[widgetId] = settings as Record<string, unknown>;
      }
    }
    return out;
  } catch {
    return {};
  }
}

/** Load settings for one widget. Returns {} if the widget has no saved values. */
export function loadWidgetSettings(widgetId: string): WidgetSettings {
  return loadAllWidgetSettings()[widgetId] ?? {};
}

/** Replace the full settings map. Fires the change event AND posts to other
 *  tabs via BroadcastChannel so all browser tabs stay in sync without
 *  needing a localStorage 'storage' event roundtrip. */
function saveAll(next: WidgetSettingsMap): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    window.dispatchEvent(new Event(EVENT_NAME));
    getBroadcastChannel()?.postMessage({ type: "save-all", payload: next });
  } catch {
    // quota / serialization — fall through; consumers still have in-memory state
  }
}

/** Set one key on one widget's settings. */
export function saveWidgetSetting(widgetId: string, key: string, value: unknown): void {
  const all = loadAllWidgetSettings();
  const current = all[widgetId] ?? {};
  all[widgetId] = { ...current, [key]: value };
  saveAll(all);
}

/** Replace one widget's settings entirely. */
export function saveWidgetSettings(widgetId: string, settings: WidgetSettings): void {
  const all = loadAllWidgetSettings();
  all[widgetId] = { ...settings };
  saveAll(all);
}

/** Remove all saved settings for one widget (revert to schema defaults). */
export function clearWidgetSettings(widgetId: string): void {
  const all = loadAllWidgetSettings();
  if (!(widgetId in all)) return;
  delete all[widgetId];
  saveAll(all);
}

/** Event consumers listen to so they can re-read after a save. */
export const WIDGET_SETTINGS_EVENT = EVENT_NAME;

/**
 * Hook: returns [settings, setKey] for one widget. `settings` is the merged
 * view (schema defaults + user overrides). `setKey(key, value)` persists +
 * fires the event so other consumers re-render.
 *
 * Pass `schema` so defaults can be applied without the consumer having to
 * import the registry directly.
 */
export function useWidgetSettings(
  widgetId: string,
  schema?: WidgetFieldSchema[],
): [WidgetSettings, (key: string, value: unknown) => void] {
  const [stored, setStored] = useState<WidgetSettings>({});

  useEffect(() => {
    if (typeof window === "undefined") return;
    const sync = () => setStored(loadWidgetSettings(widgetId));
    sync();
    window.addEventListener(EVENT_NAME, sync);

    // BroadcastChannel: receive updates from other tabs in the same browser
    // — keeps every open Command Center tab in lockstep without polling.
    const channel = getBroadcastChannel();
    const onMessage = (e: MessageEvent) => {
      if (e.data?.type === "save-all") sync();
    };
    channel?.addEventListener("message", onMessage);

    return () => {
      window.removeEventListener(EVENT_NAME, sync);
      channel?.removeEventListener("message", onMessage);
    };
  }, [widgetId]);

  // Merge schema defaults with stored values — stored wins per-key.
  const merged: WidgetSettings = {};
  if (schema) {
    for (const f of schema) {
      if (f.default !== undefined) merged[f.key] = f.default;
    }
  }
  for (const [k, v] of Object.entries(stored)) {
    merged[k] = v;
  }

  const setKey = (key: string, value: unknown) => {
    saveWidgetSetting(widgetId, key, value);
  };

  return [merged, setKey];
}
